import React, { useState, useEffect, useMemo } from "react";
import DialogBoxSkeleton from "../Common/DialogBoxSkeleton";
import { InterViewSlotService } from "../../services/InterViewSlot";
import { toast } from "react-toastify";
import { format as formatDate } from "date-fns";
import useAuth from "@/hooks/stores/useAuth";
import candidateService from "@/services/candidateService";
import { rememberContactRequestChannelId } from "@/hooks/useCandidateContactRequestChannels";
import Loader from "@/components/Loader";

const toLocalYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Merge blocks that share the same `date` so all slots (e.g. id 234 + 237 on 2026-03-30) appear together. */
const mergeBlocksByDate = (blocks = []) => {
  const byDate = new Map();
  blocks.forEach((block) => {
    const dateKey = String(block?.date || "").split("T")[0];
    if (!dateKey) return;
    if (!byDate.has(dateKey)) {
      byDate.set(dateKey, { ...block, slots: [...(block?.slots || [])] });
    } else {
      const cur = byDate.get(dateKey);
      cur.slots = [...(cur.slots || []), ...(block?.slots || [])];
    }
  });
  return Array.from(byDate.values()).map((block) => ({
    ...block,
    slots: [...(block?.slots || [])].sort((a, b) =>
      String(a?.start_time || "").localeCompare(String(b?.start_time || "")),
    ),
  }));
};

const InterviewBookingDialog = ({
  isOpen,
  onClose,
  selectedJob,
  candidateId,
  onBookingSuccess,
  isAvailabilityLoading = false,
}) => {
  const [selectedDateId, setSelectedDateId] = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [isBooking, setIsBooking] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [use24HourFormat, setUse24HourFormat] = useState(false);
  const { currentUser } = useAuth();
  const [contactForm, setContactForm] = useState({
    name: currentUser?.user?.name || "abc",
    reason: "Reschedule Interview",
    details: "",
  });

  const availabilityBlocks = useMemo(
    () => mergeBlocksByDate(selectedJob?.company_availability_blocks || []),
    [selectedJob?.company_availability_blocks],
  );

  useEffect(() => {
    console.log("Current User Data:", currentUser);
    if (isOpen && currentUser) {
      const userName = currentUser?.user?.name || currentUser?.name || "";
      setContactForm((prev) => ({ ...prev, name: userName }));
    }
  }, [isOpen, currentUser]);

  useEffect(() => {
    if (isOpen && selectedJob) {
      if (selectedJob.booked_interview_slot) {
        const bookedBlock = selectedJob.booked_interview_slot;
        const bookedSlot = bookedBlock.slots?.[0];
        setSelectedDateId(bookedBlock.id);
        setSelectedSlotId(bookedSlot?.id);
      } else {
        setSelectedSlotId(null);
        setSelectedDateId(null);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const firstBlock = availabilityBlocks?.find((b) => {
          if (!b.slots?.length || !b?.date) return false;
          const [y, m, d] = String(b.date).split("T")[0].split("-").map(Number);
          if (!y || !m || !d) return false;
          const blockDate = new Date(y, m - 1, d);
          blockDate.setHours(0, 0, 0, 0);
          return blockDate >= today;
        });
        if (firstBlock) {
          setSelectedDateId(firstBlock.id);
        }
      }
    }
  }, [isOpen, selectedJob, availabilityBlocks]);

  const handleClose = () => {
    setSelectedDateId("");
    setSelectedSlotId("");
    setIsContactFormOpen(false);
    onClose();
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    console.log("Selected Job for Contact Request:", selectedJob);

    const vacancyId = selectedJob?.vacancy?.id || selectedJob?.vacancy_id;
    const vacancyStatusId =
      selectedJob?.vacancy_status_id ?? selectedJob?.vacancystatus?.id;

    if (!vacancyId || vacancyStatusId == null) {
      console.error("Missing IDs:", {
        vacancyId,
        vacancyStatusId,
        job: selectedJob,
      });
      toast.error("Application details missing. Please refresh the page.");
      return;
    }

    const payload = {
      name: contactForm.name,
      message: contactForm.details,
      vacancy_id: vacancyId,
      vacancy_status_id: vacancyStatusId,
    };

    console.log("Sending Contact Request Payload:", payload);

    setIsBooking(true);
    try {
      const response = await candidateService.sendContactRequest(payload);
      const crId =
        response?.id ??
        response?.contact_request_id ??
        response?.contact_request?.id ??
        response?.data?.id;
      if (crId != null) rememberContactRequestChannelId(crId);
      toast.success(response?.message || "Request sent");
      handleClose();
      setContactForm((prev) => ({ ...prev, details: "" }));
    } catch (error) {
      console.error("Contact Request Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to send message";
      toast.error(errorMessage);
    } finally {
      setIsBooking(false);
    }
  };

  const handleSlotBooking = async () => {
    if (!selectedSlotId) return;

    setIsBooking(true);
    try {
      await InterViewSlotService.requestSlot(selectedSlotId);
      handleClose();
      if (onBookingSuccess) onBookingSuccess();
    } catch (error) {
      toast.error(error.message || "Failed to request slot");
    } finally {
      setIsBooking(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const date = new Date(timeStr.replace(" ", "T"));
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: !use24HourFormat,
    });
  };

  const selectedBlock =
    selectedJob?.booked_interview_slot?.id === selectedDateId
      ? selectedJob.booked_interview_slot
      : availabilityBlocks?.find(
          (b) => String(b.id) === String(selectedDateId),
        );

  const getDaysInMonth = (year, month) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compare = new Date(date);
    compare.setHours(0, 0, 0, 0);
    return compare < today;
  };

  const hasSlotsOnDate = (date) => {
    const dateString = toLocalYmd(date);
    return availabilityBlocks?.some(
      (b) =>
        b?.date && b.date.split("T")[0] === dateString && b.slots?.length > 0,
    );
  };

  const isDateSelectable = (date) =>
    hasSlotsOnDate(date) && !isPastDate(date);

  const getBlockForDate = (date) => {
    const dateString = toLocalYmd(date);
    return availabilityBlocks?.find(
      (b) => b?.date && b.date.split("T")[0] === dateString,
    );
  };

  const getFormatedDate = (dateValue) => {
    if (!dateValue) return "";
    const part = String(dateValue).split("T")[0];
    const [y, m, d] = part.split("-").map(Number);
    if (!y || !m || !d) return "";
    return formatDate(new Date(y, m - 1, d), "EEEE, dd MMM yyyy");
  };

  return (
    <DialogBoxSkeleton isOpen={isOpen} onClose={handleClose}>
      <div className="flex w-[85vw] max-w-[1100px] max-h-[85vh] overflow-y-auto overflow-x-hidden bg-card text-foreground rounded-lg shadow-xl">
        {/* Left Column: Event Info */}
        <div className="w-[30%] p-8 border-r border-border flex flex-col">
          <div className="flex flex-col gap-4">
            <h4 className="text-muted-foreground font-medium text-sm text-left">
              {selectedJob?.vacancy?.campaign?.company?.name || "Company"}
            </h4>
            <h2 className="text-2xl font-bold leading-tight text-left">
              {selectedJob?.vacancy?.title || "Interview"}
            </h2>
          </div>
        </div>

        {/* Middle Column: Calendar */}
        <div className="w-[40%] p-8 flex flex-col">
          <h3 className="text-xl font-bold mb-6 text-left">
            Select a Date & Time
          </h3>

          <div className="flex items-center justify-between mb-8">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-primary-dark hover:scale-110 text-primary-dark hover:text-white rounded-full transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h4 className="font-semibold text-lg">
              {currentMonth.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </h4>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-primary-dark hover:scale-110 text-primary-dark hover:text-white rounded-full transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
              <div
                key={day}
                className="text-center text-[10px] font-bold text-muted-foreground tracking-wider"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({
              length: getFirstDayOfMonth(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
              ),
            }).map((_, i) => (
              <div key={`empty-${i}`} className="h-10" />
            ))}
            {Array.from({
              length: getDaysInMonth(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
              ),
            }).map((_, i) => {
              const day = i + 1;
              const d = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
                day,
              );
              const hasSlots = hasSlotsOnDate(d);
              const isPast = isPastDate(d);
              const selectable = isDateSelectable(d);
              const isSelected =
                selectable &&
                selectedDateId &&
                getBlockForDate(d)?.id === selectedDateId;

              return (
                <button
                  key={day}
                  type="button"
                  disabled={!selectable}
                  aria-disabled={!selectable}
                  onClick={() => {
                    if (!selectable) return;
                    const block = getBlockForDate(d);
                    if (block) {
                      setSelectedDateId(block.id);
                      setSelectedSlotId("");
                    }
                  }}
                  className={`h-10 w-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all
                    ${
                      selectable
                        ? "text-white bg-input hover:bg-primary-dark hover:text-muted scale-110 cursor-pointer"
                        : hasSlots && isPast
                          ? "text-white bg-input opacity-40 cursor-not-allowed"
                          : "text-muted-foreground cursor-not-allowed"
                    }
                    ${isSelected ? "bg-primary-dark text-white opacity-100" : ""}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Add Contact APp TrkR button , clikcing on it should open simple form. */}
          <div className="mt-auto pt-6 border-t border-border">
            <button
              onClick={() => setIsContactFormOpen(true)}
              className="w-full py-3 px-4 flex items-center justify-center gap-2 text-primary bg-primary-dark font-semibold hover:text-primary hover:bg-primary-dark hover:scale-110 rounded-xl transition-all duration-200 group"
            >
              <svg
                className="w-5 h-5 transition-transform group-hover:scale-110"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              Contact App TrkR
            </button>
          </div>
        </div>

        {/* Right Column: Time Slots / Contact Form */}
        {!isContactFormOpen ? (
          <div className="w-[30%] p-8 overflow-y-auto border-l border-border">
            {selectedDateId ? (
              <div className="flex flex-col gap-4 text-left">
                <h4 className="text-xl font-bold text-foreground">
                  {getFormatedDate(selectedBlock?.date)}
                </h4>
                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <span className="text-sm font-medium text-foreground">
                    24Hr Format
                  </span>
                  <button
                    type="button"
                    onClick={() => setUse24HourFormat((prev) => !prev)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full border border-border transition-colors ${
                      use24HourFormat ? "bg-primary-dark border-transparent" : "bg-muted"
                    }`}
                    aria-pressed={use24HourFormat}
                    aria-label="Toggle 12h and 24h time format"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-card border border-border shadow-sm transition-transform ${
                        use24HourFormat ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                {isAvailabilityLoading ? (
                  <div className="flex min-h-[220px] items-center justify-center py-8">
                    <Loader
                      loadingText=""
                      containerClassName="bg-transparent p-0"
                      className="w-12 h-12 text-primary-dark"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {selectedBlock?.slots?.map((slot, idx) => {
                      const isSelected =
                        String(selectedSlotId) === String(slot.id);
                      return (
                        <div
                          key={slot.id ?? `slot-${idx}`}
                          className="flex items-center gap-2"
                        >
                          <button
                            onClick={() => {
                              setSelectedSlotId(slot.id);
                            }}
                            className={`text-white bg-primary-dark flex-1 min-w-0 py-3 px-3 border rounded font-bold text-sm whitespace-nowrap overflow-hidden text-ellipsis transition-all
                              ${
                                isSelected
                                  ? "bg-muted-foreground border-border text-card"
                                  : "border-primary text-primary hover:text-muted hover:bg-primary-dark "
                              }
                            `}
                          >
                            {`${formatTime(slot.start_time)} - ${formatTime(
                              slot.end_time || slot.start_time,
                            )}`}
                          </button>
                          {isSelected && (
                            <button
                              onClick={handleSlotBooking}
                              disabled={isBooking}
                              className="shrink-0 min-w-[7rem] py-3 px-3 bg-blue-600 text-white font-bold rounded hover:bg-blue-700 transition-all text-sm animate-in slide-in-from-left-2 duration-300"
                            >
                              {isBooking ? "..." : "Confirm"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : isAvailabilityLoading ? (
              <div className="flex min-h-[260px] items-center justify-center py-8">
                <Loader
                  loadingText=""
                  containerClassName="bg-transparent p-0"
                  className="w-12 h-12 text-primary"
                />
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-center">
                <svg
                  className="w-12 h-12 mb-4 opacity-20"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm">
                  Select a date to see available time slots
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="w-[30%] p-8 overflow-y-auto border-l border-border">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsContactFormOpen(false)}
                  className="p-2 hover:bg-primary hover:text-white rounded-full transition-all text-primary"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                </button>
                <h3 className="text-xl font-bold text-foreground">
                  Contact Request
                </h3>
              </div>

              <form
                onSubmit={handleContactSubmit}
                className="flex-1 flex flex-col gap-6"
              >
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-muted-foreground">
                    Your Name
                  </label>
                  <input
                    required
                    type="text"
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all shadow-sm"
                    placeholder="Enter your name"
                  />
                </div>

                <div className="space-y-2 flex-1">
                  <label className="text-sm font-semibold text-muted-foreground">
                    Reason
                  </label>
                  <textarea
                    required
                    value={contactForm.details}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        details: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all shadow-sm h-32 resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isBooking}
                  className="w-full py-3 px-4 bg-primary-dark hover:scale-110 flex items-center justify-center gap-2 text-white font-semibold hover:bg-primary-dark rounded-xl transition-all duration-200 group disabled:opacity-50"
                >
                  {isBooking ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </DialogBoxSkeleton>
  );
};

export default InterviewBookingDialog;
